import { Component, OnInit } from '@angular/core';
import {
  ICancellationStatus,
  ICreateCancellationStatus,
  ISecondaryCancellationStatus,
  IPatchCancellationStatus,
  ICreateSecondaryCancellationStatus,
  IPatchSecondaryCancellationStatus,
} from '../../interfaces/cancellation-status';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CancellationStatusService } from '../../services/cancellation-status.service';
import { compare } from 'fast-json-patch';
import { ConfirmationService, MessageService, SelectItem } from 'primeng';
import { Message } from '../../classes/Message';

@Component({
  selector: 'cancellation-statuses',
  templateUrl: './cancellation-statuses.component.html',
})
export class CancellationStatusesComponent implements OnInit {
  cancellationStatuses: ICancellationStatus[];
  showEditCancellationStatus: boolean = false;
  showEditSubCancellationStatus: boolean = false;
  cancellationStatusForm: UntypedFormGroup;
  saving: boolean = false;
  subStatusSaving: boolean = false;
  parentStatusId: number;
  selectedCancellationStatus: ICancellationStatus;
  selectedSubCancellationStatus: ISecondaryCancellationStatus;
  secondaryCancellationStatusForm: UntypedFormGroup;
  secondaryCancellationStatuses: ISecondaryCancellationStatus[];
  cancellationStatusesSelectionItems: SelectItem[] = [];

  constructor(
    private confirmationService: ConfirmationService,
    public cancellationStatusService: CancellationStatusService,
    public messageService: MessageService,
  ) {}

  ngOnInit() {
    this.initCancellationstatusForm();
    this.initSubCancellationstatusForm();
    this.getSecondaryCancellationStatuses();
    this.getCancellationStatuses();
  }

  getCancellationStatuses() {
    this.cancellationStatusService.getCancellationStatuses().then((result) => {
      this.cancellationStatuses = result;
      this.cancellationStatuses.forEach((r) =>
        this.cancellationStatusesSelectionItems.push({ label: r.name, value: r.id }),
      );
    });
  }

  initCancellationstatusForm() {
    this.cancellationStatusForm = new UntypedFormGroup({
      id: new UntypedFormControl(null),
      name: new UntypedFormControl('', Validators.compose([Validators.required])),
      description: new UntypedFormControl(''),
      isActive: new UntypedFormControl(true),
    });
  }

  addCancellationStatus() {
    this.initCancellationstatusForm();
    this.showEditCancellationStatus = true;
  }

  async saveCancellationStatus() {
    await this.createUpdateCancellationStatus();
    this.saving = false;
    this.showEditCancellationStatus = false;
    this.getCancellationStatuses();
  }

  async createUpdateCancellationStatus() {
    let f = this.cancellationStatusForm.value;
    this.saving = true;

    if (f.id == null) {
      const request: ICreateCancellationStatus = {
        name: f.name,
        description: f.description,
        isActive: f.isActive,
      };
      await this.cancellationStatusService
        .createCancellationStatus(request)
        .then(() => {})
        .catch((err) => {
          this.messageService.add(
            Message.Error(
              'An error occurred creating the cancellation status. Is it a duplicate ?',
              'Create Error',
            ),
          );
        });
      return;
    }

    const request = <IPatchCancellationStatus>{
      id: f.id,
      name: f.name,
      description: f.description,
      isActive: f.isActive,
    };

    let patch = compare({}, request);
    await this.cancellationStatusService
      .patchCancellationStatus(f.id, patch)
      .then(() => {})
      .catch((err) => {
        this.messageService.add(
          Message.Error(
            'An error occurred updating the cancellation status. Is it a duplicate ?',
            'Create Error',
          ),
        );
      });
  }

  editCancellationStatus() {
    this.cancellationStatusForm.patchValue(this.selectedCancellationStatus);
    this.showEditCancellationStatus = true;
  }

  editSecondaryCancellationStatus() {
    this.secondaryCancellationStatusForm.patchValue(this.selectedSubCancellationStatus);
    this.parentStatusId = this.selectedSubCancellationStatus.cancellationStatusId;
    this.showEditSubCancellationStatus = true;
  }

  addSecondaryCancellationStatus(cancellationStatus: ICancellationStatus) {
    this.initSubCancellationstatusForm();
    this.parentStatusId = cancellationStatus.id;
    this.secondaryCancellationStatusForm.patchValue({
      cancellationStatusId: cancellationStatus.id,
    });
    this.showEditSubCancellationStatus = true;
  }

  initSubCancellationstatusForm() {
    this.secondaryCancellationStatusForm = new UntypedFormGroup({
      id: new UntypedFormControl(null),
      cancellationStatusId: new UntypedFormControl(null),
      name: new UntypedFormControl('', Validators.compose([Validators.required])),
      showDescription: new UntypedFormControl(false),
      showToCustomer: new UntypedFormControl(false),
      isActive: new UntypedFormControl(true),
    });
  }

  getSecondaryCancellationStatuses() {
    this.cancellationStatusService.getSecondaryCancellationStatuses().then((result) => {
      this.secondaryCancellationStatuses = result;
    });
  }

  /**
   * Saves the secondary cancellation status.
   */
  async saveSecondaryCancellationStatus() {
    await this.createOrUpdateSecondaryCancellationStatus();
  }

  /**
   * Creates or updates the secondary cancellation status.
   */
  async createOrUpdateSecondaryCancellationStatus() {
    const formValue = this.secondaryCancellationStatusForm.value;

    if (this.parentStatusId !== formValue.cancellationStatusId) {
      await this.confirmAndProceed(formValue);
    } else {
      await this.continueSavingSecondaryCancellationStatus(formValue);
    }
  }

  private async confirmAndProceed(formValue) {
    this.confirmationService.confirm({
      message:
        'The selected cancellation status does not match the current parent cancellation status. Do you want to continue?',
      header: 'Confirmation',
      accept: async () => {
        try {
          await this.continueSavingSecondaryCancellationStatus(formValue);
        } catch (error) {
          console.error('Error saving secondary cancellation status:', error);
        }
      },
    });
  }

  async continueSavingSecondaryCancellationStatus(f: any) {
    this.subStatusSaving = true;

    if (f.id == null) {
      let saveObject: ICreateSecondaryCancellationStatus = {
        name: f.name,
        cancellationStatusId: f.cancellationStatusId,
        showDescription: f.showDescription,
        showToCustomer: f.showToCustomer,
        isActive: f.isActive,
      };
      await this.cancellationStatusService
        .createSecondaryCancellationStatus(saveObject)
        .then(() => {
          this.messageService.add(Message.Success('Saved successfully'));
          this.onSave();
        })
        .catch((err) => {
          this.subStatusSaving = false;
          this.messageService.add(
            Message.Error(
              'An error occurred creating the secondary cancellation status. Is it a duplicate ?',
              'Create Error',
            ),
          );
        });
      return;
    }

    let saveObject: IPatchSecondaryCancellationStatus = {
      id: f.id,
      name: f.name,
      cancellationStatusId: f.cancellationStatusId,
      showDescription: f.showDescription,
      showToCustomer: f.showToCustomer,
      isActive: f.isActive,
    };

    let patch = compare({}, saveObject);
    await this.cancellationStatusService
      .patchSecondaryCancellationStatus(f.id, patch)
      .then(() => {
        this.messageService.add(Message.Success('Updated successfully'));
        this.onSave();
      })
      .catch((err) => {
        this.subStatusSaving = false;
        this.messageService.add(
          Message.Error(
            'An error occurred updating the secondary cancellation status. Is it a duplicate ?',
            'Create Error',
          ),
        );
      });
  }

  onSave() {
    this.subStatusSaving = false;
    this.showEditSubCancellationStatus = false;
    this.getCancellationStatuses();
    this.getSecondaryCancellationStatuses();
  }
}
